import axios, { AxiosError } from 'axios';
import { ActiveFilters } from '../../hooks/useTablePageParams';
import {
  ReferralEligibility,
  ReferralPath,
  ReferralRewardStatus,
  ReferralStatus,
} from '../../enums/referral.enum';
import {
  Referral, ReferralPosition, ReferralReward, ReferralsFilters,
} from '../../@types/Referral';

// URL search-param names for the page's filters.
export const REFERRALS_PAGE_FILTERS = {
  STATUS: 'status',
  ELIGIBILITY: 'eligibility',
  UNLINKED: 'unlinked',
  PAYOUT_FAILED: 'payoutFailed',
  NO_HUBSPOT_STATUS: 'noHubspotStatus',
};

export const REFERRALS_PAGE_FILTER_KEYS = Object.values(REFERRALS_PAGE_FILTERS);

export const REFERRALS_ROWS_PER_PAGE = 25;

// The program launched in 2026, so there is no tax report before it.
const FIRST_TAX_REPORT_YEAR = 2026;

const TRUE_PARAM = 'true';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// CANCELED (Tremendous canceled the order) goes through the same resend endpoint, which re-orders it.
const RESENDABLE_REWARD_STATUSES = [
  ReferralRewardStatus.Sent,
  ReferralRewardStatus.DeliveryFailed,
  ReferralRewardStatus.Canceled,
];

// Shown as failed payouts, like account-service's payoutFailed filter (DELIVERY_FAILED, CANCELED, stuck PROCESSING).
const FAILED_PAYOUT_REWARD_STATUSES = [
  ReferralRewardStatus.DeliveryFailed,
  ReferralRewardStatus.Canceled,
  ReferralRewardStatus.Processing,
];

const QUALIFIABLE_STATUSES = [ReferralStatus.Created, ReferralStatus.NotQualified];

const isReferralStatus = (value: string): value is ReferralStatus => {
  return Object.values<string>(ReferralStatus).includes(value);
};

const isReferralEligibility = (value: string): value is ReferralEligibility => {
  return Object.values<string>(ReferralEligibility).includes(value);
};

const isOn = (activeFilters: ActiveFilters, key: string): boolean => activeFilters[key]?.[0] === TRUE_PARAM;

export const toReferralsFilters = (activeFilters: ActiveFilters): ReferralsFilters => {
  const status = activeFilters[REFERRALS_PAGE_FILTERS.STATUS]?.[0];
  const eligibility = activeFilters[REFERRALS_PAGE_FILTERS.ELIGIBILITY]?.[0];

  return {
    status: status && isReferralStatus(status) ? status : undefined,
    eligibility: eligibility && isReferralEligibility(eligibility) ? eligibility : undefined,
    unlinked: isOn(activeFilters, REFERRALS_PAGE_FILTERS.UNLINKED),
    payoutFailed: isOn(activeFilters, REFERRALS_PAGE_FILTERS.PAYOUT_FAILED),
    noHubspotStatus: isOn(activeFilters, REFERRALS_PAGE_FILTERS.NO_HUBSPOT_STATUS),
  };
};

export const toFilterParam = (isEnabled: boolean): string[] => (isEnabled ? [TRUE_PARAM] : []);

export const isOpenPosition = (position: ReferralPosition): boolean => !position.endedAt;

export const getOpenPositions = (referral: Referral): ReferralPosition[] => {
  return (referral.positions || []).filter(isOpenPosition);
};

export const isGiftCardReward = (reward: ReferralReward): boolean => reward.option === ReferralPath.GiftCard;

export const canResendReward = (reward: ReferralReward): boolean => {
  return isGiftCardReward(reward) && RESENDABLE_REWARD_STATUSES.includes(reward.status);
};

// A canceled gift card is re-issued: a new order for the same amount, to the original email or a new one.
export const isReissueReward = (reward: ReferralReward): boolean => {
  return isGiftCardReward(reward) && reward.status === ReferralRewardStatus.Canceled;
};

export const isFailedPayoutReward = (reward: ReferralReward): boolean => {
  return FAILED_PAYOUT_REWARD_STATUSES.includes(reward.status);
};

// A gift card still PROCESSING is held (breaker, kill switch, config) or its order failed; force-send retries it now.
export const canForceSendReward = (reward: ReferralReward): boolean => {
  return isGiftCardReward(reward) && reward.status === ReferralRewardStatus.Processing;
};

export const canQualify = (referral: Referral): boolean => QUALIFIABLE_STATUSES.includes(referral.status);

export const canOverrideEligibility = (referral: Referral): boolean => {
  return referral.eligibility === ReferralEligibility.Ineligible;
};

export const isValidEmail = (email: string): boolean => EMAIL_PATTERN.test(email.trim());

export const getTaxReportYears = (now: Date): number[] => {
  const years: number[] = [];
  for (let year = Math.max(now.getFullYear(), FIRST_TAX_REPORT_YEAR); year >= FIRST_TAX_REPORT_YEAR; year -= 1) {
    years.push(year);
  }

  return years;
};

// account-service's ReferralProgramDisabledError (404): REFERRAL_PROGRAM_ENABLED is off in this environment.
const REFERRAL_PROGRAM_DISABLED_ERROR_CODE = 4017;

// account-service's ReferralHubspotUnavailableError (503). Its error code is the generic external-service one, so the
// message is the stable key.
const REFERRAL_HUBSPOT_UNAVAILABLE_MESSAGE = 'REFERRAL_HUBSPOT_UNAVAILABLE';

export enum ReferralErrorKind {
  ProgramDisabled = 'PROGRAM_DISABLED',
  HubspotUnavailable = 'HUBSPOT_UNAVAILABLE',
  Other = 'OTHER',
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.length > 0;

// axios 0.21's isAxiosError throws on null, which is what a query without an error holds.
const isAxiosRequestError = (error: unknown): error is AxiosError => isRecord(error) && axios.isAxiosError(error);

const getErrorBody = (error: unknown): Record<string, unknown> => {
  const body: unknown = isAxiosRequestError(error) ? error.response?.data : undefined;

  return isRecord(body) ? body : {};
};

// account-service sends reasons as strings; the gateway's own validation errors may be { field, error } objects.
const toFieldErrorText = (fieldError: unknown): string | undefined => {
  if (isNonEmptyString(fieldError)) {
    return fieldError;
  }

  if (!isRecord(fieldError)) {
    return undefined;
  }

  const { field, error } = fieldError;
  if (!isNonEmptyString(error)) {
    return undefined;
  }

  return isNonEmptyString(field) ? `${field}: ${error}` : error;
};

export const getReferralErrorKind = (error: unknown): ReferralErrorKind => {
  const { internalErrorCode, message } = getErrorBody(error);

  if (internalErrorCode === REFERRAL_PROGRAM_DISABLED_ERROR_CODE) {
    return ReferralErrorKind.ProgramDisabled;
  }

  if (message === REFERRAL_HUBSPOT_UNAVAILABLE_MESSAGE) {
    return ReferralErrorKind.HubspotUnavailable;
  }

  return ReferralErrorKind.Other;
};

// The server's message with its reasons appended, e.g. "REFERRAL_ACTION_NOT_ALLOWED (NOT_PROCESSING)".
export const getReferralErrorDetail = (error: unknown): string => {
  if (!isAxiosRequestError(error)) {
    return error instanceof Error ? error.message : String(error);
  }

  const { message, fieldsErrors } = getErrorBody(error);
  const errorMessage = isNonEmptyString(message) ? message : error.message;
  const reasons = (Array.isArray(fieldsErrors) ? fieldsErrors : [])
    .map(toFieldErrorText)
    .filter(isNonEmptyString);

  return reasons.length ? `${errorMessage} (${reasons.join(', ')})` : errorMessage;
};

export const saveBlobAsFile = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
